// App.js - Enhanced Real Estate Form (No Tailwind)
import emailjs from '@emailjs/browser';
import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  // Form data state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    propertyType: '',
    bedrooms: '',
    notes: '',
    intent: ''
  });

  // UI state
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form validation state
  const [fieldErrors, setFieldErrors] = useState({});

  // Background image setup (commented out to avoid issues)
  useEffect(() => {
    document.body.style.backgroundImage = `url('https://lh3.googleusercontent.com/d/1fedBU-F1Qux4b1Z4bMpJUh1VZY3K7Ptw=s0')`;
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'center';
    document.body.style.backgroundAttachment = 'fixed';
    
    return () => {
      document.body.style.backgroundImage = '';
      document.body.style.backgroundSize = '';
      document.body.style.backgroundPosition = '';
      document.body.style.backgroundAttachment = '';
    };
    
  }, []);

  // Form validation functions
  const validateStep = (step) => {
    const errors = {};
    
    if (step === 1) {
      if (!formData.name.trim()) errors.name = 'Le nom est requis';
      if (!formData.email.trim()) errors.email = 'L\'email est requis';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Format d\'email invalide';
      if (!formData.phone.trim()) errors.phone = 'Le numéro de téléphone est requis';
    }
    
    if (step === 2) {
      if (!formData.address.trim()) errors.address = 'L\'adresse est requise';
      if (!formData.propertyType) errors.propertyType = 'Le type de propriété est requis';
      if (!formData.bedrooms) errors.bedrooms = 'Le nombre de chambres est requis';
    }
    
    if (step === 3) {
      if (!formData.intent) errors.intent = 'La raison de votre demande est requise';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Form handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      // Scroll to top of form container
      const formContainer = document.querySelector('.form-container');
      if (formContainer) {
        formContainer.scrollTop = 0;
      }
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    setError(null);
    // Scroll to top of form container
    const formContainer = document.querySelector('.form-container');
    if (formContainer) {
      formContainer.scrollTop = 0;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Final validation
    if (!validateStep(3)) {
      return;
    }

    setIsLoading(true);
    setError(null);
  
    try {
      // Geographic filter validation
      const allowedPostalPrefixes = ['G1', 'G2', 'G3'];
      const addressUpper = (formData.address || '').toUpperCase();
      const postalMatch = allowedPostalPrefixes.some(prefix => addressUpper.includes(prefix));

      if (!postalMatch) {
        setError("Désolé, nous ne desservons actuellement que la région de Québec et Lévis (codes postaux G1, G2 et G3).");
        setIsLoading(false);
        return;
      }

// Save to database via API
const response = await fetch('https://myrealestate.duckdns.org/api/submit', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    address: formData.address,
    propertyType: formData.propertyType,
    bedrooms: formData.bedrooms,
    notes: formData.notes,
    intent: formData.intent
  })
});

const data = await response.json();

if (!response.ok) {
  throw new Error(data.error || 'Submission failed');
}

console.log("Form data saved with ID:", data.submissionId);
  
// Create separate object for EmailJS (with camelCase field names)
const emailData = {
  name: formData.name,
  email: formData.email,
  phone: formData.phone,
  address: formData.address,
  propertyType: formData.propertyType,
  bedrooms: formData.bedrooms,
  notes: formData.notes,
  intent: formData.intent,
  submissionId: data.submissionid
};
  
// Send notification email using EmailJS
await emailjs.send(
  'service_gk5lqtf',
  'template_6vqdb9w',
  emailData,
  '2SBgUopeRvFS9re8F'
);
console.log('Notification email sent successfully');

      // Send confirmation email to client
      await emailjs.send(
        'service_gk5lqtf',
        'template_h5152x7',
        {
          name: formData.name,
          email: formData.email,
          address: formData.address,
          submissionId: data.submissionid
        },
        '2SBgUopeRvFS9re8F'
      );
      console.log('Confirmation email sent to client');

      // Success - show thank you page
      setSubmitted(true);
      setIsLoading(false);
      
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);
      
      // More specific error messages
      if (error.code === 'permission-denied') {
        setError("Erreur de permissions. Veuillez réessayer plus tard.");
      } else if (error.message.includes('network')) {
        setError("Problème de connexion réseau. Vérifiez votre connexion internet et réessayez.");
      } else {
        setError("Une erreur inattendue s'est produite. Veuillez réessayer ou nous contacter directement (imob.forms@gmail.com).");
      }
      
      setIsLoading(false);
    }
  };

  // Calculate progress percentage
  const progress = Math.round((currentStep / 3) * 100);

  // Field error display component
  const FieldError = ({ field }) => {
    if (!fieldErrors[field]) return null;
    return <div className="field-error">{fieldErrors[field]}</div>;
  };

  return (
    <div className="app">
      <header className="header">
        <div className="header-content">
          <h1>Évaluation de la valeur de votre maison</h1>
          <p>
            Complétez ce formulaire pour obtenir une évaluation gratuite de la valeur de votre maison sur le marché, 
            effectuée par des professionnels en présentiel (rencontre de 30 à 45 minutes)
          </p>
        </div>
      </header>

      {!submitted ? (
        <div className="form-container">
          {/* Progress Bar */}
          <div className="progress-container">
            <div className="progress-info">
              <span className="step-text">Étape {currentStep} sur 3</span>
              <span className="progress-percentage">{progress}% Complété</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-bar-fill" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
          
          {/* Error Display */}
          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Step 1: Basic Info */}
            {currentStep === 1 && (
              <div className="form-step form-step-active">
                <h2>Parlez-nous de vous !</h2>
                
                <div className="form-group">
                  <label htmlFor="name">Nom complet *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Jean Dupont"
                    className={fieldErrors.name ? 'error' : ''}
                  />
                  <FieldError field="name" />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Courriel - Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="jean.dupont@exemple.com"
                    className={fieldErrors.email ? 'error' : ''}
                  />
                  <FieldError field="email" />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Numéro de téléphone *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 418 123-4567"
                    className={fieldErrors.phone ? 'error' : ''}
                  />
                  <FieldError field="phone" />
                </div>

                <div className="button-group">
                  <div></div> {/* Empty div for spacing */}
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn btn-primary"
                  >
                    Continuer →
                  </button>
                </div>
              </div>
            )}
            
            {/* Step 2: Property Details */}
            {currentStep === 2 && (
              <div className="form-step form-step-active">
                <h2>Détails de la propriété</h2>
                
                <div className="form-group">
                  <label htmlFor="address">Adresse complète de la propriété *</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="123 Rue Saint-Jean, Québec, QC G1X 3Y7"
                    className={fieldErrors.address ? 'error' : ''}
                  />
                  <p className="help-text">
                    Format: numéro civique, rue, ville, province, code postal. 
                    Nous desservons uniquement la région de Québec (codes postaux G1, G2 et G3).
                  </p>
                  <FieldError field="address" />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="propertyType">Type de propriété *</label>
                    <select
                      id="propertyType"
                      name="propertyType"
                      value={formData.propertyType}
                      onChange={handleChange}
                      className={fieldErrors.propertyType ? 'error' : ''}
                    >
                      <option value="">Choisir...</option>
                      <option value="house">Maison unifamiliale</option>
                      <option value="condo">Condominium</option>
                      <option value="duplex">Duplex</option>
                      <option value="triplex">Triplex</option>
                      <option value="multiplex">Multiplex (4+ logements)</option>
                      <option value="townhouse">Maison de ville</option>
                    </select>
                    <FieldError field="propertyType" />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="bedrooms">Nombre de chambres *</label>
                    <select
                      id="bedrooms"
                      name="bedrooms"
                      value={formData.bedrooms}
                      onChange={handleChange}
                      className={fieldErrors.bedrooms ? 'error' : ''}
                    >
                      <option value="">Choisir...</option>
                      <option value="1">1 chambre</option>
                      <option value="2">2 chambres</option>
                      <option value="3">3 chambres</option>
                      <option value="4">4 chambres</option>
                      <option value="5">5 chambres</option>
                      <option value="6+">6+ chambres</option>
                    </select>
                    <FieldError field="bedrooms" />
                  </div>
                </div>

                <div className="button-group">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="btn btn-secondary"
                  >
                    ← Retour
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn btn-primary"
                  >
                    Continuer →
                  </button>
                </div>
              </div>
            )}
            
            {/* Step 3: Additional Info & Privacy Consent */}
            {currentStep === 3 && (
              <div className="form-step form-step-active">
                <h2>Détails finaux</h2>
                
                <div className="form-group">
                  <label htmlFor="intent">Quelle est la raison de votre demande? *</label>
                  <select
                    id="intent"
                    name="intent"
                    value={formData.intent}
                    onChange={handleChange}
                    className={fieldErrors.intent ? 'error' : ''}
                  >
                    <option value="">Choisir...</option>
                    <option value="sell">Je veux vendre ma propriété</option>
                    <option value="refinance">Je veux refinancer mon hypothèque</option>
                    <option value="insurance">Pour des fins d'assurance</option>
                    <option value="estate">Succession / règlement de succession</option>
                    <option value="curiosity">Juste par curiosité</option>
                    <option value="investment">Évaluation d'investissement</option>
                    <option value="other">Autre raison</option>
                  </select>
                  <FieldError field="intent" />
                </div>
                
                <div className="form-group">
                  <label htmlFor="notes">Notes additionnelles (optionnel)</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Décrivez des détails particuliers de votre propriété, des rénovations récentes, ou toute autre information pertinente..."
                  ></textarea>
                  <p className="help-text">
                    Partagez tout détail qui pourrait aider nos experts à mieux évaluer votre propriété.
                  </p>
                </div>
                <div className="button-group">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="btn btn-secondary"
                  >
                    ← Retour
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn btn-primary"
                  >
                    {isLoading ? (
                      <>
                        <span>Traitement en cours...</span>
                        <div className="loading-spinner"></div>
                      </>
                    ) : (
                      '🏠 Obtenir mon évaluation gratuite'
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      ) : (
        <div className="thank-you">
          <div className="success-icon">✓</div>
          <h2>🎉 Merci beaucoup !</h2>
          <p>
            <strong>Votre demande d'évaluation a été reçue avec succès.</strong>
          </p>
          <p>
            Un expert immobilier certifié de notre équipe vous contactera dans les 
            <strong> 24 à 48 heures</strong> pour planifier votre évaluation gratuite en présentiel.
          </p>
          <p>
            <strong>Prochaines étapes :</strong>
          </p>
          <ul style={{textAlign: 'left', maxWidth: '400px', margin: '20px auto'}}>
            <li>Vérifiez votre courriel pour la confirmation</li>
            <li>Un expert vous appellera pour fixer un rendez-vous</li>
            <li>Rendez-vous de 30-45 minutes à votre domicile</li>
            <li>Rapport d'évaluation détaillé remis sur place</li>
          </ul>
          <p className="small-text">
            <strong>Important :</strong> Vérifiez vos courriels (y compris les courriers indésirables) 
            pour notre message de confirmation.
          </p>
        </div>
      )}
      
      <footer className="footer">
        <p>© 2025 Évaluations Résidentielles au Québec • Tous droits réservés • Service professionnel et confidentiel</p>
      </footer>
    </div>
  );
}

export default App;